/* Same-tab, short-lived handoff. Personal words never travel in URLs or analytics. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) {
    root.WoteRitualHandoff = api;
    const start = () => api.mount(root);
    // Deferred scripts still see "interactive" before DOMContentLoaded. Wait
    // for the earlier commerce listener to restore/lock any recovered order.
    if (root.document.readyState !== 'complete') root.document.addEventListener('DOMContentLoaded', start);
    else start();
  }
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const KEY = 'woteRitualDraftV1';
  const TTL = 2 * 60 * 60 * 1000;

  function readDraft(storage, token, handle, now = Date.now()) {
    if (!storage || !token || !handle) return null;
    try {
      const draft = JSON.parse(storage.getItem(KEY) || 'null');
      if (!draft || draft.version !== 1 || !Number.isFinite(draft.createdAt) || draft.createdAt > now || now - draft.createdAt > TTL) {
        if (draft) storage.removeItem(KEY);
        return null;
      }
      if (draft.id !== token || draft.productHandle !== handle || !/^[a-z0-9-]+$/.test(handle)) return null;
      if (typeof draft.intention !== 'string' || draft.intention.length > 1600 || !draft.intention.trim()) return null;
      if (typeof draft.context !== 'string' || draft.context.length > 650 || ![1, 2, 3].includes(draft.depth)) return null;
      return draft;
    } catch (_) { return null; }
  }

  function personalization(draft) {
    // Preserve the exact client text, including line breaks and spelling.
    return draft.intention + (draft.context ? '\n\nMy choices in Find My Ritual:\n' + draft.context : '');
  }

  function mayApply(field, wrapper) {
    return Boolean(field && !field.disabled && !field.value.trim() && !wrapper.classList.contains('is-order-added'));
  }

  function mount(win) {
    const doc = win.document;
    let token;
    try { token = new URL(win.location.href).searchParams.get('ritual_draft'); } catch (_) { return; }
    if (!token) return;
    let storage;
    try { storage = win.sessionStorage; } catch (_) {}
    doc.querySelectorAll('[data-personalized-form]').forEach((wrapper) => {
      if (wrapper.querySelector('[data-ritual-handoff]')) return;
      const field = wrapper.querySelector('textarea[name="properties[What I want to improve]"]');
      if (!field) return;
      const draft = readDraft(storage, token, wrapper.dataset.productHandle);
      const banner = doc.createElement('aside');
      banner.className = 'ritual-handoff';
      banner.dataset.ritualHandoff = '';
      const heading = doc.createElement('p');
      heading.className = 'ritual-handoff__title';
      heading.textContent = 'Your intention, in your own words.';
      const status = doc.createElement('p');
      status.setAttribute('role', 'status');
      const actions = doc.createElement('div');
      actions.className = 'ritual-handoff__actions';
      banner.append(heading, status, actions);
      wrapper.prepend(banner);
      const makeButton = (label, action) => {
        const button = doc.createElement('button');
        button.type = 'button'; button.textContent = label;
        button.addEventListener('click', action);
        actions.append(button);
        return button;
      };
      const review = () => {
        if (!wrapper.classList.contains('flow-at-final')) wrapper.querySelector('[data-personalization-next="2"]')?.click();
        if (wrapper.classList.contains('flow-at-final') && !field.disabled) field.focus();
      };
      if (!draft) {
        status.textContent = 'Your private draft is no longer available in this tab. Write or paste your intention below before adding to your bag.';
        makeButton('Write my intention', review);
        makeButton('Dismiss', () => banner.remove());
        return;
      }
      const value = personalization(draft);
      let applied = false;
      const apply = () => {
        if (!mayApply(field, wrapper)) return false;
        field.value = value;
        applied = true;
        field.dispatchEvent(new win.Event('input', { bubbles: true }));
        status.textContent = 'Your words and choices have been carried into your personalization. Please review them; everything is still editable.';
        return true;
      };
      apply();
      if (!applied) status.textContent = 'Your existing personalization has been kept. Your Find My Ritual draft is available below if you want to copy from it.';
      makeButton('Review my personalization', review);
      const details = doc.createElement('details');
      const summary = doc.createElement('summary'); summary.textContent = 'Read my guide draft';
      const words = doc.createElement('p'); words.className = 'ritual-handoff__words'; words.textContent = value;
      details.append(summary, words); banner.append(details);
      if (!applied && !field.disabled) makeButton('Use draft in an empty field', () => {
        if (!apply()) status.textContent = 'Your current words were not replaced. Clear the personalization field yourself first if you want to use this draft.';
        review();
      });
      makeButton('Discard guide draft', () => {
        try { storage.removeItem(KEY); } catch (_) {}
        // A manual edit belongs to the client, even when it began with our draft.
        if (applied && !field.disabled && field.value === value) {
          field.value = '';
          field.dispatchEvent(new win.Event('input', { bubbles: true }));
        }
        details.remove(); actions.replaceChildren();
        status.textContent = 'The guide draft has been removed from this tab. Any changes you made in the personalization field have been kept.';
        makeButton('Review my personalization', review);
      });
    });
  }
  return { KEY, TTL, readDraft, personalization, mayApply, mount };
});
